"use client"

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Car, LogIn, Shield, Users } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export default function UnifiedLoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated && user) {
      const redirectTo = user.role === 'ADMIN' || user.role === 'manager' ? '/' : '/staff/dashboard';
      navigate(redirectTo);
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await login({ email, password });
      
      if (result.status === 'success' && result.data) {
        toast.success('Login successful!');
        // Role-based redirection
        const redirectTo = result.data.user.role === 'ADMIN' || result.data.user.role === 'manager' 
          ? '/' 
          : '/staff/dashboard';
        navigate(redirectTo);
      } else {
        // Show specific error message from API response
        const errorMessage = result.message || 'Invalid email or password';
        
        // Determine if it's an email or password error based on message content
        const lowerMessage = errorMessage.toLowerCase();
        let displayMessage = errorMessage;
        
        if (lowerMessage.includes('email') || lowerMessage.includes('user not found') || lowerMessage.includes('user does not exist')) {
          displayMessage = 'Invalid email';
        } else if (lowerMessage.includes('password') || lowerMessage.includes('incorrect password') || lowerMessage.includes('wrong password')) {
          displayMessage = 'Invalid password';
        } else if (lowerMessage.includes('invalid') || lowerMessage.includes('unauthorized') || lowerMessage.includes('401')) {
          // Generic invalid credentials - show a user-friendly message
          displayMessage = 'Invalid email or password';
        }
        
        toast.error(displayMessage);
        setIsSubmitting(false);
      }
    } catch (error: any) {
      // Extract error message from error object
      const errorMessage = 
        error?.response?.data?.message || 
        error?.response?.data?.error ||
        error?.message || 
        'An error occurred during login. Please try again.';
      
      // Determine if it's an email or password error
      const lowerMessage = errorMessage.toLowerCase();
      let displayMessage = errorMessage;
      
      if (lowerMessage.includes('email') || lowerMessage.includes('user not found')) {
        displayMessage = 'Invalid email';
      } else if (lowerMessage.includes('password') || lowerMessage.includes('incorrect password')) {
        displayMessage = 'Invalid password';
      } else if (lowerMessage.includes('invalid') || lowerMessage.includes('unauthorized')) {
        displayMessage = 'Invalid email or password';
      }
      
      toast.error(displayMessage);
      setIsSubmitting(false);
    }
  };

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border-0 shadow-sm">
        <CardHeader className="space-y-4 text-center pb-6">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-xl bg-primary/10">
            <Car className="h-8 w-8 text-primary" />
          </div>
          <div>
            <CardTitle className="text-2xl font-semibold">Welcome Back</CardTitle>
            <CardDescription className="text-sm mt-2">WeClean Car Wash Management System</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@weclean.com.qa"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                required
                className="h-11"
                autoComplete="off"
              />
            </div>
            <Button type="submit" className="w-full h-11 mt-6" disabled={isSubmitting}>
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                  Logging in...
                </span>
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          <p className="text-xs text-center text-muted-foreground pt-2">
            © {new Date().getFullYear()} WeClean Car Wash
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
